import { PdfWatermarkMode, type PdfWatermarkSettings, type PdfWatermarkSettingsRequest } from '@today/types'
import { Button } from '@today/ui-primitives'
import { useState } from 'react'
import {
	draftOf,
	type PdfWatermarkDraft,
	type PdfWatermarkFieldErrors,
	pickImage,
	submissionOf,
} from './pdf-watermark-draft'
import { ImagePicker, ImagePlacement, ModeChoice, TextFields } from './pdf-watermark-fields'

export type { PdfWatermarkFieldErrors } from './pdf-watermark-draft'

/**
 * Props for {@link PdfWatermarkForm}.
 *
 * @property settings - The stored settings; the form is seeded from them once (key the form on `updatedAt` to re-seed).
 * @property readOnly - The manifest grants READ: every control is disabled and no save is offered.
 * @property isBusy - A save is in flight; saving is disabled.
 * @property fieldErrors - API field path → message, shown next to that field.
 * @property error - A refusal to announce for the whole form.
 * @property onSave - Reports the settings to store (a whole replacement).
 */
export interface PdfWatermarkFormProps {
	settings: PdfWatermarkSettings
	readOnly?: boolean
	isBusy?: boolean
	fieldErrors?: PdfWatermarkFieldErrors
	error?: string | null
	onSave: (input: PdfWatermarkSettingsRequest) => void
}

const NO_ERRORS: PdfWatermarkFieldErrors = {}

/**
 * The tenant's offer-PDF watermark (TO-495 REQ-011): disabled, a text line with
 * insertable tokens and an optional diagonal stamp, or an image at an opacity
 * and position.
 *
 * Pure apart from reading the picked file: the page's hook saves; the server
 * owns every rule but the image type and size, checked here before any
 * request, and a refusal arrives as `fieldErrors` (by API path) or `error`.
 */
export function PdfWatermarkForm({
	settings,
	readOnly = false,
	isBusy = false,
	fieldErrors = NO_ERRORS,
	error,
	onSave,
}: PdfWatermarkFormProps) {
	const [draft, setDraft] = useState(() => draftOf(settings))
	const [imageError, setImageError] = useState<string | null>(null)
	const update = (patch: Partial<PdfWatermarkDraft>) => setDraft((current) => ({ ...current, ...patch }))
	const fields = { draft, readOnly, errors: fieldErrors, update }

	const pick = async (file: File) => {
		const { image, error: refusal } = await pickImage(file)
		setImageError(refusal)
		update({ image })
	}

	const save = () => {
		const submission = submissionOf(draft)
		if (submission) onSave(submission)
		else setImageError((current) => current ?? 'Choose an image')
	}

	return (
		<section className="flex flex-col gap-6" aria-label="PDF watermark">
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<ModeChoice {...fields} />
			{draft.mode === PdfWatermarkMode.TEXT ? <TextFields {...fields} /> : null}
			{draft.mode === PdfWatermarkMode.IMAGE ? (
				<div className="flex flex-col gap-3">
					<ImagePicker {...fields} imageError={imageError} onPick={(file) => file && void pick(file)} />
					<ImagePlacement {...fields} />
				</div>
			) : null}
			{readOnly ? null : (
				<div>
					<Button variant="primary" isDisabled={isBusy} onPress={save}>
						Save watermark
					</Button>
				</div>
			)}
		</section>
	)
}
